import {
  TYCOON_TILES,
  TYCOON_JAIL_FEE
} from '../../../tycoon-engine.js';
import { queryOne } from '../../core/dom.js';
import { tycoonAvatarLabel } from '../../../shared/tycoon-avatars.js';
import { tycoonAvatarSvg, tycoonAvatarOptionsMarkup } from './avatar-art.js';
import {
  TYCOON_GROUP_COLORS,
  tycoonGridPosition,
  formatTycoonMoney,
  tycoonTileIcon,
  tycoonTileMeta
} from './presentation.js';

const PLAYER_COLORS = ['#e53935', '#1565c0', '#2e7d32', '#7b1fa2'];

function playerVisualIndex(player, fallbackIndex = 0) {
  const seatIndex = Number(player?.seatIndex);
  return Number.isInteger(seatIndex) && seatIndex >= 0 && seatIndex < PLAYER_COLORS.length
    ? seatIndex
    : fallbackIndex;
}

export function normalizeSelectedAsset(game, selectedAsset) {
  if (!game?.currentPlayer) return null;
  const owned = game.propertiesOf(game.currentPlayer.id);
  const candidate = Number(selectedAsset);
  return owned.includes(candidate) ? candidate : (owned[0] ?? null);
}

export function renderTycoonBoard({
  game,
  selectedAsset,
  onSelectAsset = () => {}
}) {
  const root = queryOne('#tycoonBoard');
  root.innerHTML = '';

  const center = document.createElement('div');
  center.className = 'tycoon-center';
  center.innerHTML = `
    <div class="tycoon-center-sky" aria-hidden="true">✈︎　🚂　⚓</div>
    <div class="tycoon-center-card chance" aria-hidden="true">?</div>
    <div class="tycoon-center-card community" aria-hidden="true">★</div>
    <div class="tycoon-center-title">CỜ TỶ PHÚ</div>
    <div class="tycoon-center-brand">IVS GAMES · VIỆT NAM</div>
    <div class="tycoon-center-copy">Mua địa danh · Xây nhà & khách sạn · Quản trị tài sản</div>
  `;

  const legend = document.createElement('div');
  legend.className = 'tycoon-center-legend';
  legend.setAttribute('aria-label', 'Chú thích chủ sở hữu, công trình và thế chấp');

  const playerKey = document.createElement('div');
  playerKey.className = 'tycoon-center-player-key';
  game.players.forEach((player, playerIndex) => {
    const visualIndex = playerVisualIndex(player, playerIndex);
    const item = document.createElement('span');
    item.className = 'tycoon-center-player-key-item';
    item.title = `Người chơi ${playerIndex + 1}: ${player.name}`;

    const token = document.createElement('span');
    token.className = 'tycoon-center-player-key-token';
    token.style.setProperty('--token-color', PLAYER_COLORS[visualIndex % PLAYER_COLORS.length]);
    token.innerHTML = tycoonAvatarSvg(player.avatarId, { fallbackIndex: visualIndex });
    token.setAttribute('aria-hidden', 'true');

    const name = document.createElement('span');
    name.className = 'tycoon-center-player-key-name';
    name.textContent = player.name;

    item.append(token, name);
    playerKey.appendChild(item);
  });

  const statusKey = document.createElement('div');
  statusKey.className = 'tycoon-center-status-key';
  statusKey.innerHTML = `
    <span>🏠×n Nhà</span>
    <span>🏨 Khách sạn</span>
    <span><b>TC</b> Thế chấp</span>
  `;

  legend.append(playerKey, statusKey);
  center.appendChild(legend);
  root.appendChild(center);

  TYCOON_TILES.forEach((tile, index) => {
    const cell = document.createElement('button');
    cell.type = 'button';
    cell.className = 'tycoon-tile ' + tile.type;
    if (tile.name.length >= 12) cell.classList.add('name-long');
    if (tile.name.length >= 16) cell.classList.add('name-xlong');
    if ([0, 10, 20, 30].includes(index)) cell.classList.add('corner');
    if (selectedAsset === index) cell.classList.add('selected');
    const isAssetTile = ['property', 'railroad', 'utility'].includes(tile.type);
    if (isAssetTile) {
      cell.classList.add('inspectable');
      cell.setAttribute('aria-haspopup', 'dialog');
    }
    cell.setAttribute('role', 'gridcell');
    cell.setAttribute('aria-label', tile.name);

    const pos = tycoonGridPosition(index);
    cell.style.gridRow = String(pos.row);
    cell.style.gridColumn = String(pos.col);
    if (game.currentPlayer?.position === index) cell.classList.add('current');

    if (tile.group && TYCOON_GROUP_COLORS[tile.group]) {
      const band = document.createElement('span');
      band.className = 'tycoon-color-band';
      band.style.background = TYCOON_GROUP_COLORS[tile.group];
      cell.appendChild(band);
    }

    const asset = game.propertyState(index);
    const owner = game.ownerOf(index);
    let ownerIndex = -1;
    let ownerVisualIndex = -1;

    if (owner) {
      ownerIndex = game.players.findIndex(player => player.id === owner.id);
      ownerVisualIndex = playerVisualIndex(owner, Math.max(0, ownerIndex));
      const ownerColor = PLAYER_COLORS[ownerVisualIndex % PLAYER_COLORS.length];
      cell.style.setProperty('--owner-color', ownerColor);
      cell.classList.add('owned');
      if (asset?.mortgaged) cell.classList.add('mortgaged');
      if (asset?.houses > 0 && tile.type === 'property') cell.classList.add('has-development');

      const ownerMark = document.createElement('span');
      ownerMark.className = 'tycoon-owner';
      ownerMark.textContent = `P${ownerVisualIndex + 1}`;
      ownerMark.title = `Tài sản của ${owner.name} · P${ownerVisualIndex + 1}`;
      ownerMark.setAttribute('aria-hidden', 'true');
      cell.appendChild(ownerMark);
    }

    const icon = tycoonTileIcon(tile);
    if (icon) {
      const iconEl = document.createElement('span');
      iconEl.className = 'tycoon-tile-icon';
      iconEl.textContent = icon;
      cell.appendChild(iconEl);
    }

    const name = document.createElement('span');
    name.className = 'tycoon-tile-name';
    name.textContent = tile.name;

    const meta = document.createElement('span');
    meta.className = 'tycoon-tile-meta';
    meta.textContent = tycoonTileMeta(tile, index, game);
    cell.append(name, meta);

    if (owner && asset?.mortgaged) {
      const status = document.createElement('span');
      status.className = 'tycoon-asset-status is-mortgaged';
      status.textContent = 'TC · THẾ CHẤP';
      cell.appendChild(status);
    } else if (owner && asset?.houses > 0 && tile.type === 'property') {
      const status = document.createElement('span');
      status.className = asset.houses >= 5
        ? 'tycoon-asset-status is-hotel'
        : 'tycoon-asset-status is-house';
      status.textContent = asset.houses >= 5 ? '🏨 KHÁCH SẠN' : `🏠 ×${asset.houses}`;
      cell.appendChild(status);
    }

    const accessibility = [tile.name];
    if (meta.textContent) accessibility.push(meta.textContent);
    if (owner) accessibility.push(`Chủ sở hữu: ${owner.name}, P${ownerVisualIndex + 1}`);
    if (asset?.mortgaged) {
      accessibility.push('Đang thế chấp');
    } else if (asset?.houses >= 5 && tile.type === 'property') {
      accessibility.push('Có khách sạn');
    } else if (asset?.houses > 0 && tile.type === 'property') {
      accessibility.push(`Có ${asset.houses} nhà`);
    }
    const accessibleText = accessibility.join(' · ');
    cell.title = accessibleText;
    cell.setAttribute('aria-label', accessibleText);

    const tokens = document.createElement('div');
    tokens.className = 'tycoon-token-row';
    game.players.forEach((player, playerIndex) => {
      if (player.bankrupt || player.position !== index) return;
      const visualIndex = playerVisualIndex(player, playerIndex);
      const token = document.createElement('span');
      token.className = 'tycoon-token';
      token.style.setProperty('--token-color', PLAYER_COLORS[visualIndex % PLAYER_COLORS.length]);
      token.innerHTML = tycoonAvatarSvg(player.avatarId, { fallbackIndex: visualIndex });
      const avatarLabel = tycoonAvatarLabel(player.avatarId, visualIndex);
      token.title = `${player.name} · ${avatarLabel}`;
      token.setAttribute('role', 'img');
      token.setAttribute('aria-label', `${player.name}, nhân vật ${avatarLabel}, đang ở ${tile.name}`);
      tokens.appendChild(token);
    });
    cell.appendChild(tokens);

    cell.addEventListener('click', () => onSelectAsset(index, owner?.id === game.currentPlayer.id));
    root.appendChild(cell);
  });
}


export function showTycoonAssetInspector(game, index) {
  const dialog = queryOne('#tycoonAssetDialog');
  const title = queryOne('#tycoonAssetDialogTitle');
  const ownerBox = queryOne('#tycoonAssetDialogOwner');
  const detail = queryOne('#tycoonAssetDialogDetail');
  const hint = queryOne('#tycoonAssetDialogHint');
  const parsedIndex = Number(index);
  const tile = Number.isInteger(parsedIndex) ? TYCOON_TILES[parsedIndex] : null;

  if (!dialog || !title || !ownerBox || !detail || !hint || !game || !tile) return false;
  if (!['property', 'railroad', 'utility'].includes(tile.type)) return false;

  const asset = game.propertyState(parsedIndex);
  const owner = game.ownerOf(parsedIndex);
  const ownerIndex = owner ? game.players.findIndex(player => player.id === owner.id) : -1;
  const ownerVisualIndex = owner ? playerVisualIndex(owner, Math.max(0, ownerIndex)) : -1;
  const rollTotal = game.lastRoll ? game.lastRoll[0] + game.lastRoll[1] : 7;
  const currentRent = game.getRent(parsedIndex, rollTotal);
  const typeLabel = tile.type === 'property'
    ? 'Địa danh'
    : tile.type === 'railroad'
      ? 'Vận tải'
      : 'Tiện ích';

  title.textContent = tile.name;

  ownerBox.innerHTML = '';
  const ownerBadge = document.createElement('span');
  ownerBadge.className = 'tycoon-asset-dialog-owner-badge';
  if (owner) {
    ownerBadge.textContent = `P${ownerVisualIndex + 1}`;
    ownerBadge.style.setProperty('--owner-color', PLAYER_COLORS[ownerVisualIndex % PLAYER_COLORS.length]);
  } else {
    ownerBadge.textContent = '—';
    ownerBadge.classList.add('unowned');
  }

  const ownerCopy = document.createElement('div');
  const ownerLabel = document.createElement('small');
  ownerLabel.textContent = owner ? 'Chủ sở hữu' : 'Trạng thái';
  const ownerName = document.createElement('strong');
  ownerName.textContent = owner ? owner.name : 'Chưa có chủ';
  ownerCopy.append(ownerLabel, ownerName);
  ownerBox.append(ownerBadge, ownerCopy);

  detail.innerHTML = '';
  const addRow = (label, value) => {
    const row = document.createElement('div');
    row.className = 'tycoon-asset-dialog-row';
    const key = document.createElement('span');
    key.textContent = label;
    const val = document.createElement('strong');
    val.textContent = value;
    row.append(key, val);
    detail.appendChild(row);
  };

  addRow('Loại tài sản', typeLabel);
  addRow('Giá mua', formatTycoonMoney(tile.price));
  addRow('Tiền thuê hiện tại', asset?.mortgaged ? '0 ₫ · đang thế chấp' : formatTycoonMoney(currentRent));

  if (tile.type === 'property') {
    const development = asset?.houses >= 5
      ? 'Khách sạn'
      : asset?.houses > 0
        ? `${asset.houses} nhà`
        : 'Chưa xây';
    const group = game.groupIndices(tile.group);
    const groupOwned = owner
      ? group.filter(groupIndex => game.ownerOf(groupIndex)?.id === owner.id).length
      : 0;
    addRow('Công trình', development);
    addRow('Bộ màu', owner ? `${groupOwned}/${group.length} địa danh cùng chủ` : `0/${group.length} địa danh`);
    addRow('Chi phí xây', formatTycoonMoney(tile.houseCost));
  }

  addRow('Thế chấp', formatTycoonMoney(game.mortgageValue(parsedIndex)));
  addRow('Chuộc tài sản', formatTycoonMoney(game.unmortgageCost(parsedIndex)));

  if (owner?.id === game.currentPlayer?.id) {
    hint.textContent = 'Tài sản của người đang tới lượt. Có thể quản lý xây dựng, bán công trình hoặc thế chấp tại bảng Quản lý tài sản.';
  } else if (owner) {
    hint.textContent = `Tài sản thuộc ${owner.name}. Bạn có thể xem thông tin nhưng chỉ chủ sở hữu mới được quản lý khi đến lượt.`;
  } else {
    hint.textContent = 'Tài sản chưa có chủ. Khi dừng tại ô này, người chơi có thể mua nếu luật và số dư cho phép.';
  }

  if (typeof dialog.showModal === 'function') {
    if (!dialog.open) dialog.showModal();
  } else {
    dialog.setAttribute('open', '');
  }
  return true;
}

export function renderTycoonPlayers(game) {
  const root = queryOne('#tycoonPlayers');
  root.innerHTML = '';

  game.players.forEach((player, index) => {
    const visualIndex = playerVisualIndex(player, index);
    const row = document.createElement('div');
    row.className = 'tycoon-player';
    if (index === game.turnIndex && !game.finished) row.classList.add('active');
    if (player.bankrupt) row.classList.add('bankrupt');

    const token = document.createElement('span');
    token.className = 'tycoon-player-token';
    token.style.setProperty('--token-color', PLAYER_COLORS[visualIndex % PLAYER_COLORS.length]);
    token.innerHTML = tycoonAvatarSvg(player.avatarId, { fallbackIndex: visualIndex });
    token.title = `${player.name} · ${tycoonAvatarLabel(player.avatarId, visualIndex)}`;

    const copy = document.createElement('div');
    copy.className = 'tycoon-player-copy';

    const title = document.createElement('strong');
    title.textContent = player.name + (player.bankrupt ? ' · Phá sản' : player.inJail ? ' · Đang ở tù' : '');

    const sub = document.createElement('small');
    sub.textContent = `P${visualIndex + 1} · ${tycoonAvatarLabel(player.avatarId, visualIndex)} · ${game.propertiesOf(player.id).length} tài sản · Giá trị ròng ${formatTycoonMoney(game.netWorth(player))}`;
    copy.append(title, sub);

    const cash = document.createElement('span');
    cash.className = 'tycoon-player-cash';
    cash.textContent = formatTycoonMoney(player.cash);

    row.append(token, copy, cash);
    root.appendChild(row);
  });
}

export function renderTycoonAvatarCustomizer({
  game,
  selectedPlayerIndex = 0,
  hidden = false
}) {
  const card = queryOne('#tycoonAvatarCard');
  const select = queryOne('#tycoonAvatarPlayerSelect');
  const options = queryOne('#tycoonLocalAvatarOptions');
  if (!card || !select || !options || !game) return 0;

  const safeIndex = Math.max(0, Math.min(game.players.length - 1, Math.trunc(Number(selectedPlayerIndex) || 0)));
  card.classList.toggle('hidden', Boolean(hidden));
  if (hidden) return safeIndex;

  const previousValue = String(safeIndex);
  select.innerHTML = game.players
    .map((player, index) => `<option value="${index}">P${playerVisualIndex(player, index) + 1} · ${player.name}</option>`)
    .join('');
  select.value = previousValue;

  const player = game.players[safeIndex];
  const visualIndex = playerVisualIndex(player, safeIndex);
  options.innerHTML = tycoonAvatarOptionsMarkup({
    selectedId: player.avatarId,
    dataAttribute: 'data-tycoon-local-avatar'
  });

  const note = queryOne('#tycoonAvatarNote');
  if (note) {
    note.textContent = `P${visualIndex + 1}: ${tycoonAvatarLabel(player.avatarId, visualIndex)} · Chọn con vật khác để đổi nhân vật. Nếu đang được người khác dùng, hai người sẽ đổi avatar cho nhau.`;
  }

  return safeIndex;
}

export function renderTycoonLog(game) {
  const root = queryOne('#tycoonLog');
  root.innerHTML = '';

  const entries = game.log.slice(-24).reverse();
  if (!entries.length) {
    root.innerHTML = '<div class="empty-state">Chưa có sự kiện.</div>';
    return;
  }

  entries.forEach(message => {
    const row = document.createElement('div');
    row.className = 'tycoon-log-entry';
    row.textContent = message;
    row.title = message;
    root.appendChild(row);
  });
}

export function renderTycoonManagement({
  game,
  selectedAsset,
  allowManage
}) {
  const select = queryOne('#tycoonAssetSelect');
  const detail = queryOne('#tycoonAssetDetail');
  const buildBtn = queryOne('#tycoonBuildBtn');
  const sellBtn = queryOne('#tycoonSellBuildingBtn');
  const mortgageBtn = queryOne('#tycoonMortgageBtn');
  const unmortgageBtn = queryOne('#tycoonUnmortgageBtn');
  const buildHint = queryOne('#tycoonBuildHint');
  if (!select || !detail || !game) return selectedAsset;

  const current = game.currentPlayer;
  const owned = game.propertiesOf(current.id);
  selectedAsset = normalizeSelectedAsset(game, selectedAsset);

  select.innerHTML = '';

  if (!owned.length) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = 'Chưa có tài sản';
    select.appendChild(option);
    select.disabled = true;
    detail.textContent = 'Mua tài sản để mở quản lý xây dựng và thế chấp.';
  } else {
    select.disabled = false;

    owned.forEach(index => {
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = TYCOON_TILES[index].name;
      select.appendChild(option);
    });

    select.value = String(selectedAsset);

    const index = selectedAsset;
    const tile = TYCOON_TILES[index];
    const asset = game.propertyState(index);
    const rent = game.getRent(index, game.lastRoll ? game.lastRoll[0] + game.lastRoll[1] : 7);
    const development = tile.type === 'property'
      ? (asset.houses === 5 ? 'Khách sạn' : asset.houses ? `${asset.houses} nhà` : 'Chưa xây')
      : 'Không áp dụng';
    const group = tile.type === 'property' ? game.groupIndices(tile.group) : [];
    const ownedGroup = group.filter(groupIndex => game.ownerOf(groupIndex)?.id === current.id);
    const groupCopy = tile.type === 'property'
      ? `<span>Bộ màu: ${ownedGroup.length}/${group.length} địa danh · Chi phí xây: ${formatTycoonMoney(tile.houseCost)}/cấp</span>`
      : '';

    detail.innerHTML = `
      <strong>${tile.name}</strong>
      <span>Loại: ${tile.type === 'property' ? 'Địa danh' : tile.type === 'railroad' ? 'Vận tải' : 'Tiện ích'}</span>
      <span>Trạng thái: ${asset.mortgaged ? 'Đang thế chấp' : development}</span>
      ${groupCopy}
      <span>Tiền thuê hiện tại: ${formatTycoonMoney(rent)}</span>
      <span>Thế chấp: ${formatTycoonMoney(game.mortgageValue(index))} · Chuộc: ${formatTycoonMoney(game.unmortgageCost(index))}</span>
    `;
  }

  const index = selectedAsset;
  const tile = index === null ? null : TYCOON_TILES[index];
  const asset = index === null ? null : game.propertyState(index);
  const canBuild = Boolean(allowManage && index !== null && game.canBuild(index));
  const canSell = Boolean(allowManage && index !== null && game.canSellBuilding(index));
  const canMortgage = Boolean(allowManage && index !== null && game.canMortgage(index));
  const canUnmortgage = Boolean(allowManage && index !== null && game.canUnmortgage(index));

  if (buildBtn) buildBtn.disabled = !canBuild;
  if (sellBtn) sellBtn.disabled = !canSell;
  if (mortgageBtn) mortgageBtn.disabled = !canMortgage;
  if (unmortgageBtn) unmortgageBtn.disabled = !canUnmortgage;

  let buildMessage = '';
  let buildTitle = 'Xây nhà / khách sạn';

  if (!allowManage) {
    buildMessage = 'Chỉ người đang tới lượt mới có thể xây dựng hoặc quản lý tài sản.';
  } else if (index === null || !tile || !asset) {
    buildMessage = 'Mua đủ một bộ màu để bắt đầu xây nhà.';
  } else if (tile.type !== 'property') {
    buildMessage = 'Vận tải và tiện ích không thể xây nhà hoặc khách sạn.';
  } else {
    const group = game.groupIndices(tile.group);
    const missing = group.filter(groupIndex => game.ownerOf(groupIndex)?.id !== current.id);
    const mortgaged = group.filter(groupIndex => game.propertyState(groupIndex)?.mortgaged);
    const minLevel = Math.min(...group.map(groupIndex => game.propertyState(groupIndex)?.houses || 0));
    const lowest = group.filter(groupIndex => (game.propertyState(groupIndex)?.houses || 0) === minLevel);
    const nextIsHotel = asset.houses === 4;
    const nextLabel = nextIsHotel ? 'khách sạn' : `nhà thứ ${asset.houses + 1}`;
    buildTitle = `Xây ${nextIsHotel ? 'khách sạn' : 'nhà'} · ${formatTycoonMoney(tile.houseCost)}`;

    if (game.phase === 'debt') {
      buildMessage = 'Đang xử lý nợ: hãy bán công trình hoặc thế chấp tài sản trước.';
    } else if (asset.mortgaged) {
      buildMessage = `${tile.name} đang thế chấp. Hãy chuộc tài sản trước khi xây.`;
    } else if (missing.length) {
      buildMessage = `Chưa đủ bộ màu (${group.length - missing.length}/${group.length}). Còn thiếu: ${missing.map(groupIndex => TYCOON_TILES[groupIndex].name).join(', ')}.`;
    } else if (mortgaged.length) {
      buildMessage = `Phải chuộc toàn bộ tài sản trong bộ màu trước khi xây: ${mortgaged.map(groupIndex => TYCOON_TILES[groupIndex].name).join(', ')}.`;
    } else if (asset.houses >= 5) {
      buildMessage = `${tile.name} đã đạt cấp tối đa: khách sạn.`;
    } else if (asset.houses !== minLevel) {
      buildMessage = `Phải xây đồng đều. Hãy xây trước tại: ${lowest.map(groupIndex => TYCOON_TILES[groupIndex].name).join(', ')}.`;
    } else if (current.cash < tile.houseCost) {
      buildMessage = `Không đủ tiền mặt. Cần ${formatTycoonMoney(tile.houseCost)}, hiện có ${formatTycoonMoney(current.cash)}.`;
    } else {
      buildMessage = `Có thể xây ${nextLabel} tại ${tile.name} với ${formatTycoonMoney(tile.houseCost)}.`;
    }
  }

  if (buildBtn) {
    buildBtn.textContent = canBuild ? `＋ ${buildTitle}` : '＋ Xây nhà / khách sạn';
    buildBtn.title = buildMessage || buildTitle;
  }
  if (buildHint) {
    buildHint.textContent = buildMessage;
    buildHint.classList.toggle('is-ready', canBuild);
    buildHint.classList.toggle('is-blocked', !canBuild && Boolean(buildMessage));
  }

  return selectedAsset;
}


export function renderTycoonHud({
  game,
  selectedAsset,
  isOnline,
  canAct,
  roomCode,
  onlineState,
  onlineRole,
  onlineUserSeat,
  myPlayerId,
  pendingRequestId,
  statusEl,
  turnSubtextEl,
  modeBadgeEl
}) {
  const player = game.currentPlayer;
  const winner = game.players.find(item => item.id === game.winnerId) || null;
  selectedAsset = normalizeSelectedAsset(game, selectedAsset);

  const phaseCopy = {
    roll: player.inJail ? 'Đang ở tù · Tung đôi, trả phí hoặc dùng thẻ' : 'Sẵn sàng tung xúc xắc',
    buy: 'Quyết định mua tài sản',
    debt: 'Cần thanh toán nợ · Bán nhà hoặc thế chấp tài sản',
    end: game.extraRoll ? 'Ra đôi · Kết thúc xử lý để tung thêm' : 'Kết thúc lượt để chuyển người chơi',
    finished: 'Ván chơi kết thúc'
  };

  queryOne('#tycoonTurnName').textContent = winner ? winner.name + ' thắng' : player.name;
  queryOne('#tycoonTurnCash').textContent = formatTycoonMoney(player.cash);
  queryOne('#tycoonRound').textContent = String(game.round);
  queryOne('#tycoonPhaseText').textContent = phaseCopy[game.phase] || '';
  queryOne('#tycoonPlayerCount').value = String(game.players.length);

  if (game.lastRoll) {
    const dice = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
    queryOne('#tycoonDice').textContent = game.lastRoll.map(value => dice[value - 1]).join(' ');
  } else {
    queryOne('#tycoonDice').textContent = '🎲 🎲';
  }

  const isRoll = game.phase === 'roll' && !game.finished && (!isOnline || canAct);
  const isBuy = game.phase === 'buy' && !game.finished && (!isOnline || canAct);
  const isDebt = game.phase === 'debt' && !game.finished && (!isOnline || canAct);
  const isEnd = game.phase === 'end' && !game.finished && (!isOnline || canAct);

  queryOne('#tycoonRollBtn').classList.toggle('hidden', !isRoll);
  queryOne('#tycoonBuyBtn').classList.toggle('hidden', !isBuy);
  queryOne('#tycoonSkipBtn').classList.toggle('hidden', !isBuy);
  queryOne('#tycoonEndTurnBtn').classList.toggle('hidden', !isEnd);
  queryOne('#tycoonPayDebtBtn').classList.toggle('hidden', !isDebt);
  queryOne('#tycoonBankruptBtn').classList.toggle('hidden', !isDebt);
  queryOne('#tycoonPayJailBtn').classList.toggle('hidden', !(isRoll && player.inJail));
  queryOne('#tycoonUseJailCardBtn').classList.toggle('hidden', !(isRoll && player.inJail && player.jailCards > 0));

  if (isBuy && game.pendingProperty !== null) {
    const tile = TYCOON_TILES[game.pendingProperty];
    queryOne('#tycoonBuyBtn').textContent = 'Mua ' + tile.name + ' · ' + formatTycoonMoney(tile.price);
  }

  if (isDebt && game.pendingDebt) {
    queryOne('#tycoonPayDebtBtn').textContent = `Thanh toán ${formatTycoonMoney(game.pendingDebt.amount)}`;
    queryOne('#tycoonPayDebtBtn').disabled = player.cash < game.pendingDebt.amount;
  } else {
    queryOne('#tycoonPayDebtBtn').disabled = false;
  }

  queryOne('#tycoonPayJailBtn').disabled = player.cash < TYCOON_JAIL_FEE;
  queryOne('#tycoonUseJailCardBtn').textContent = `Dùng thẻ ra tù (${player.jailCards})`;

  const eventCard = queryOne('#tycoonEventCard');
  if (eventCard) {
    if (game.lastEvent?.text) {
      eventCard.classList.remove('hidden');
      eventCard.querySelector('strong').textContent = game.lastEvent.deck === 'chance' ? 'CƠ HỘI' : 'KHÍ VẬN';
      eventCard.querySelector('span').textContent = game.lastEvent.text;
    } else {
      eventCard.classList.add('hidden');
    }
  }

  statusEl.textContent = winner ? winner.name + ' chiến thắng' : 'Lượt ' + player.name;

  if (isOnline) {
    modeBadgeEl.textContent = 'Trực tuyến LIVE';
    queryOne('#tycoonOnlineRoomCode').textContent = roomCode || onlineState?.room?.code || '—';
    queryOne('.tycoon-setup-row').classList.add('hidden');
    queryOne('#tycoonOnlineRoomActions').classList.remove('hidden');

    const myPlayer = myPlayerId ? game.players.find(item => item.id === myPlayerId) : null;
    const activePlayer = Boolean(
      onlineRole === 'player' &&
      myPlayer &&
      !myPlayer.bankrupt &&
      onlineState?.status === 'active'
    );

    queryOne('#tycoonOnlineResignBtn').classList.toggle('hidden', !activePlayer);
    queryOne('#tycoonOnlineResignBtn').disabled = !activePlayer || Boolean(pendingRequestId);

    const rematchBtn = queryOne('#tycoonOnlineRematchBtn');
    rematchBtn.classList.toggle('hidden', onlineState?.status !== 'finished' || !Number.isInteger(onlineUserSeat));

    if (onlineState?.status === 'finished' && Number.isInteger(onlineUserSeat)) {
      const requested = Boolean(onlineState?.rematchSeats?.[String(onlineUserSeat)]);
      rematchBtn.textContent = requested ? 'Đã yêu cầu chơi lại...' : 'Yêu cầu chơi lại';
      rematchBtn.disabled = requested;
    }

    if (winner) {
      turnSubtextEl.textContent = onlineState?.result?.reason || 'Ván Cờ tỷ phú LIVE kết thúc';
    } else if (onlineRole === 'spectator') {
      turnSubtextEl.textContent = 'Đang xem trực tiếp';
    } else if (canAct) {
      turnSubtextEl.textContent = '👉 Đến lượt của bạn · ' + phaseCopy[game.phase];
    } else if (myPlayer?.bankrupt) {
      turnSubtextEl.textContent = 'Bạn đã rời ván · Có thể tiếp tục xem';
    } else {
      turnSubtextEl.textContent = 'Đang chờ ' + player.name + ' thực hiện lượt...';
    }
  } else {
    queryOne('.tycoon-setup-row').classList.remove('hidden');
    queryOne('#tycoonOnlineRoomActions').classList.add('hidden');
    turnSubtextEl.textContent = winner ? 'Ván Cờ tỷ phú kết thúc' : phaseCopy[game.phase];
  }

  return selectedAsset;
}
